import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { toast } from 'sonner'
import { useAuth } from '@/context/AuthContext'
import { api } from '@/lib/api'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { EmptyState } from '@/components/shared/EmptyState'
import type { DashboardData } from '@/types'
import {
  Globe, Mail, Send, Inbox, TrendingUp,
  AlertTriangle, ChevronRight, Zap, CreditCard
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid
} from 'recharts'

interface AnalyticsData {
  daily: { date: string; sent: number; delivered: number; bounced: number }[]
  by_domain: { domain: string; sent: number }[]
  by_recipient_domain: { domain: string; sent: number }[]
}

interface PlanSummary {
  slug: string
  name: string
  price_cents: number
}

// CHECKPOINT: PRD-10 FR-10.9 — Overview dashboard with usage cards, charts, and recent activity.
export function Overview() {
  const { user } = useAuth()
  const [data, setData] = useState<DashboardData | null>(null)
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
  const [plans, setPlans] = useState<PlanSummary[]>([])
  const [chartData, setChartData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [verifying, setVerifying] = useState(false)

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        // CHECKPOINT: PRD-10 FR-10.9.2 — Load 30-day analytics for usage charts
        const today = new Date()
        const thirtyDaysAgo = new Date(today)
        thirtyDaysAgo.setDate(today.getDate() - 29)
        const date_from = thirtyDaysAgo.toISOString().split('T')[0]
        const date_to = today.toISOString().split('T')[0]

        // Try to fetch dashboard data; fall back to building from individual endpoints
        let dashboardData: DashboardData
        let analyticsRes: any = { data: { daily: [], by_domain: [], by_recipient_domain: [] } }
        let plansRes: any = { data: { plans: [] } }
        try {
          const [dashRes, analytics, plansResponse] = await Promise.all([
            api.get('/account/dashboard'),
            api
              .get('/account/analytics/sends', { params: { date_from, date_to } })
              .catch(() => ({ data: { daily: [], by_domain: [], by_recipient_domain: [] } })),
            api.get('/billing/plans').catch(() => ({ data: { plans: [] } })),
          ])
          dashboardData = dashRes.data
          analyticsRes = analytics
          plansRes = plansResponse
        } catch {
          // Build dashboard from individual endpoints
          const [accountRes, planRes, domainsRes, mailboxesRes, sendsRes, inboxRes, analyticsFallback, plansFallback] = await Promise.all([
            api.get('/account/me').catch(() => ({ data: null })),
            api.get('/account/plan').catch(() => ({ data: null })),
            api.get('/domains').catch(() => ({ data: { domains: [] } })),
            api.get('/mailboxes').catch(() => ({ data: { mailboxes: [] } })),
            api.get('/outbound/messages?per_page=5').catch(() => ({ data: { messages: [] } })),
            api.get('/inbound/messages?per_page=5').catch(() => ({ data: { messages: [] } })),
            api
              .get('/account/analytics/sends', { params: { date_from, date_to } })
              .catch(() => ({ data: { daily: [], by_domain: [], by_recipient_domain: [] } })),
            api.get('/billing/plans').catch(() => ({ data: { plans: [] } })),
          ])

          analyticsRes = analyticsFallback
          plansRes = plansFallback

          const account = accountRes.data
          const planData = planRes.data
          const domains = domainsRes.data.domains || []
          const mailboxes = mailboxesRes.data.mailboxes || []
          const sends = sendsRes.data.messages || []
          const inbound = inboxRes.data.messages || []

          const plan = account?.plan || 'starter'
          const usage = account?.usage || {}
          const limits = planData?.limits || {}
          const domainLimit = usage.domains_limit || limits.domains || 0
          const mailboxLimit = usage.mailboxes_limit || limits.mailboxes || 0
          const emailsYearlyLimit = limits.emails_yearly || usage.emails_limit || 0
          const storageLimit = usage.storage_limit_mb || limits.storage_mb || 0

          // CHECKPOINT: PRD-10 FR-10.9.1 — Build fallback usage from individual endpoints
          dashboardData = {
            account: {
              id: account?.id || '',
              email: account?.email || '',
              display_name: account?.display_name || null,
              plan,
              status: account?.status || 'active',
              email_verified: account?.email_verified || false,
              timezone: account?.timezone || 'UTC',
              created_at: account?.created_at || '',
              current_period_end: account?.current_period_end,
            },
            usage: {
              domains: { used: usage.domains_used || domains.length, limit: domainLimit, percentage: domainLimit > 0 ? Math.min(100, Math.round(((usage.domains_used || domains.length) / domainLimit) * 100)) : 0 },
              mailboxes: { used: usage.mailboxes_used || mailboxes.length, limit: mailboxLimit, percentage: mailboxLimit > 0 ? Math.min(100, Math.round(((usage.mailboxes_used || mailboxes.length) / mailboxLimit) * 100)) : 0 },
              // Fallback uses the yearly count since plans bill yearly
              emails_sent_this_year: usage.emails_sent_this_year || 0,
              emails_limit: emailsYearlyLimit > 0 ? emailsYearlyLimit : 1,
              emails_percentage: emailsYearlyLimit > 0 ? Math.min(100, Math.round(((usage.emails_sent_this_year || 0) / emailsYearlyLimit) * 100)) : 0,
              storage_mb: { used: usage.storage_used_mb || 0, limit: storageLimit, percentage: storageLimit > 0 ? Math.min(100, Math.round(((usage.storage_used_mb || 0) / storageLimit) * 100)) : 0 },
            },
            recent_sends: sends,
            recent_inbound: inbound,
            alerts: account?.email_verified ? [] : [{ type: 'warning', message: 'Please verify your email address' }],
          }
        }
        setData(dashboardData)
        setAnalytics(analyticsRes.data)
        setPlans(plansRes.data.plans || [])

        const daily = analyticsRes.data?.daily || []
        // Map to last 30 days for the chart, filling missing days with 0
        const last30 = []
        for (let i = 29; i >= 0; i--) {
          const d = new Date()
          d.setDate(d.getDate() - i)
          const dateStr = d.toISOString().split('T')[0]
          const found = daily.find((x: any) => x.date === dateStr)
          last30.push({
            day: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            sent: found?.sent || 0,
            delivered: found?.delivered || 0,
            bounced: found?.bounced || 0,
          })
        }
        setChartData(last30)
      } catch {
        // CHECKPOINT: PRD-10 FR-10.13.4 — Show error state when dashboard data fails to load
        setError(true)
      } finally {
        setLoading(false)
      }
    }
    fetchDashboard()
  }, [])

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-accent rounded" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-accent rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-[260px] bg-accent rounded-xl" />
          <div className="h-[260px] bg-accent rounded-xl" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-[200px] bg-accent rounded-xl" />
          <div className="h-[200px] bg-accent rounded-xl" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <EmptyState
        title="Failed to load dashboard"
        description="We couldn't load your overview data. Please try refreshing the page."
        icon={<AlertTriangle className="h-5 w-5" />}
      />
    )
  }

  const usage = data?.usage

  // CHECKPOINT: PRD-10 FR-10.9.3 — 80% usage threshold upgrade prompt
  const planSlug = data?.account?.plan || 'starter'
  const currentPlan = plans.find((p) => p.slug === planSlug)
  const upgradePlan = plans.find(
    (p) => p.slug !== planSlug && p.price_cents > (currentPlan?.price_cents || 0)
  )
  const usageEntries = usage
    ? [
        { key: 'Domains', pct: usage.domains.percentage },
        { key: 'Mailboxes', pct: usage.mailboxes.percentage },
        { key: 'Emails', pct: usage.emails_percentage },
        { key: 'Storage', pct: usage.storage_mb.percentage },
      ]
    : []
  const highestUsage = usageEntries.reduce(
    (max, u) => (u.pct > max.pct ? u : max),
    usageEntries[0] || { key: '', pct: 0 }
  )
  const showUpgradeBanner = Boolean(upgradePlan && highestUsage.pct >= 80)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Welcome back, {user?.display_name || user?.email}
          </p>
        </div>
        <Link to="/domains">
          <Button size="sm" className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-lg shadow-purple-500/20">
            <Globe className="mr-2 h-4 w-4" />
            Add Domain
          </Button>
        </Link>
      </div>

      {/* Email Verification Banner */}
      {user && !user.email_verified && (
        <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-4 w-4 text-blue-400 shrink-0" />
            <p className="text-sm text-blue-300">
              Please verify your email address to unlock all features.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              setVerifying(true)
              try {
                await api.post('/auth/resend-verification', { email: user.email })
                toast.success('Verification email sent! Check your inbox.')
              } catch {
                toast.error('Failed to send verification email')
              } finally {
                setVerifying(false)
              }
            }}
            disabled={verifying}
          >
            {verifying ? 'Sending...' : 'Resend Email'}
          </Button>
        </div>
      )}

      {/* Subscription Banner */}
      {user?.status === 'onboarding' && (
        <div className="rounded-lg border border-purple-500/30 bg-purple-500/10 px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Zap className="h-5 w-5 text-purple-400 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-purple-200">
                You're on the free preview.
              </p>
              <p className="text-xs text-purple-300/70">
                Subscribe to unlock mailboxes, email sending, and receiving.
              </p>
            </div>
          </div>
          <Link to="/billing">
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
              <CreditCard className="mr-1.5 h-3.5 w-3.5" />
              Subscribe
            </Button>
          </Link>
        </div>
      )}

      {/* Upgrade Banner */}
      {showUpgradeBanner && upgradePlan && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-5 w-5 text-amber-400 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-200">
                You're at {highestUsage.pct}% of your {highestUsage.key.toLowerCase()} limit.
              </p>
              <p className="text-xs text-amber-300/70">
                Upgrade to {upgradePlan.name} for more {highestUsage.key.toLowerCase()} and additional features.
              </p>
            </div>
          </div>
          <Link to="/billing">
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
              <Zap className="mr-1.5 h-3.5 w-3.5" />
              Upgrade
            </Button>
          </Link>
        </div>
      )}

      {/* Usage Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <UsageCard
          icon={<Globe className="h-4 w-4 text-violet-400" />}
          label="Domains"
          used={usage?.domains.used || 0}
          limit={usage?.domains.limit || 0}
          percentage={usage?.domains.percentage || 0}
          href="/domains"
        />
        <UsageCard
          icon={<Mail className="h-4 w-4 text-blue-400" />}
          label="Mailboxes"
          used={usage?.mailboxes.used || 0}
          limit={usage?.mailboxes.limit || 0}
          percentage={usage?.mailboxes.percentage || 0}
          href="/mailboxes"
        />
        <UsageCard
          icon={<Send className="h-4 w-4 text-emerald-400" />}
          label="Emails This Year"
          used={usage?.emails_sent_this_year || 0}
          limit={usage?.emails_limit || 1}
          percentage={usage?.emails_percentage || 0}
          href="/sending"
        />
        <UsageCard
          icon={<Zap className="h-4 w-4 text-amber-400" />}
          label="Storage"
          used={usage?.storage_mb.used || 0}
          limit={usage?.storage_mb.limit || 1}
          percentage={usage?.storage_mb.percentage || 0}
          href="/billing"
          unit="MB"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Emails Sent (30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                  <Bar dataKey="sent" fill="url(#sentGradient)" radius={[4, 4, 0, 0]} />
                  <defs>
                    <linearGradient id="sentGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8b5cf6" />
                      <stop offset="100%" stopColor="#7c3aed" />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Delivery Rate (30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                  />
                  <Line type="monotone" dataKey="delivered" stroke="#22c55e" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="sent" stroke="#8b5cf6" strokeWidth={2} dot={false} strokeDasharray="4 4" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CHECKPOINT: PRD-10 FR-10.9.2 — Top sending and recipient domain charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Top Sending Domains
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              {analytics && analytics.by_domain.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[...analytics.by_domain.slice(0, 5)].reverse()}
                    layout="vertical"
                    margin={{ left: 16, right: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis type="category" dataKey="domain" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" width={120} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                    <Bar dataKey="sent" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-muted-foreground h-full flex items-center justify-center">
                  No sending data for the last 30 days
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Top Recipient Domains
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              {analytics && analytics.by_recipient_domain.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[...analytics.by_recipient_domain.slice(0, 5)].reverse()}
                    layout="vertical"
                    margin={{ left: 16, right: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis type="category" dataKey="domain" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" width={120} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                    <Bar dataKey="sent" fill="#22c55e" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-muted-foreground h-full flex items-center justify-center">
                  No recipient data for the last 30 days
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Recent Sends</CardTitle>
            <Link to="/sending" className="text-xs text-primary hover:underline flex items-center">
              View all <ChevronRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent>
            {data?.recent_sends && data.recent_sends.length > 0 ? (
              <div className="space-y-2">
                {data.recent_sends.map((msg) => (
                  <div key={msg.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{msg.subject}</p>
                      <p className="text-xs text-muted-foreground">To: {msg.to?.map((r: any) => typeof r === 'string' ? r : r.email || r).join(', ')}</p>
                    </div>
                    <StatusBadge status={msg.status} />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No emails sent yet"
                description="Send your first email via the API or dashboard."
                icon={<Send className="h-5 w-5" />}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Recent Inbound</CardTitle>
            <Link to="/inbox" className="text-xs text-primary hover:underline flex items-center">
              View all <ChevronRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent>
            {data?.recent_inbound && data.recent_inbound.length > 0 ? (
              <div className="space-y-2">
                {data.recent_inbound.map((msg) => (
                  <div key={msg.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{msg.subject || '(no subject)'}</p>
                      <p className="text-xs text-muted-foreground">From: {msg.from_addr?.email}</p>
                    </div>
                    {!msg.is_read && <span className="h-2 w-2 rounded-full bg-blue-400" />}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No inbound emails yet"
                description="Emails sent to your domains will appear here."
                icon={<Inbox className="h-5 w-5" />}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function UsageCard({ icon, label, used, limit, percentage, href, unit = '' }: {
  icon: React.ReactNode
  label: string
  used: number
  limit: number
  percentage: number
  href: string
  unit?: string
}) {
  const isUnlimited = limit < 0
  return (
    <Link to={href} className="block">
      <Card className="hover:bg-accent/30 transition-colors cursor-pointer group">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-accent flex items-center justify-center">
                {icon}
              </div>
              <span className="text-sm font-medium text-muted-foreground">{label}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <div className="flex items-baseline gap-1 mb-2">
            <span className="text-2xl font-bold">{used}</span>
            {!isUnlimited && (
              <span className="text-sm text-muted-foreground">/ {limit}{unit}</span>
            )}
            {isUnlimited && <span className="text-sm text-muted-foreground">unlimited</span>}
          </div>
          {!isUnlimited && (
            <div className="space-y-1">
              <Progress value={percentage} className="h-1.5" />
              <p className="text-[11px] text-muted-foreground">{percentage}% used</p>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  )
}
